import React, { useEffect, useLayoutEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { AppView, CardView, Indicator, rem, SCREEN_BOTTOM_PADDING, withAnimatedNavigationHeader, } from 'design-system';
import ListEmptyView from 'design-system/ListEmptyView';
import NavigationHeaderTitle from 'design-system/NavigationHeaderTitle';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatDateForItem } from 'utils/formatting';
import { HEADER_WITH_SUBTITLE_HEIGHT } from 'navigators/styles';
import ScrollingLineChart from 'design-system/ScrollingLineChart/ScrollingLineChart';
import Section from 'design-system/Section';
import LoanItemsList from 'features/borrow/components/LoanItemsList';
import CreditScoreHistoryFactors from '../components/credit-score-history/CreditScoreHistoryFactors';
import CreditScoreHistoryHeader from '../components/credit-score-history/CreditScoreHistoryHeader';
import { selectCreditScoreHistoryView, selectIsCreditScoreHistoryFetching, selectLatestCreditScoreHistoryDate, } from '../selectors';
import { isWeb } from '../../../constants';
import { getCreditScoreHistory } from '../actions';
const strings = {
    headerTitle: 'Credit history',
    emptyTitle: 'No credit history found',
    emptySubTitle: "We couldn't load your credit history from our provider",
};
const Footer = () => (<Section title="Our offers" style={styles.section}>
    <LoanItemsList excludeKeys={['broadband', 'energy']}/>
  </Section>);
const CreditScoreHistoryScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const lastUpdateDate = useAppSelector(selectLatestCreditScoreHistoryDate);
    const isFetching = useAppSelector(selectIsCreditScoreHistoryFetching);
    const { dataPoints, factorsByIndex, groupCount } = useAppSelector(selectCreditScoreHistoryView);
    // Only group when we have enough data to make it worthwhile. Below
    // these thresholds the chart looks better with one label per point.
    const enableGrouping = dataPoints.length > 6 && groupCount > 1;
    const groupingProps = enableGrouping
        ? { grouped: true, showLastPointLabel: true }
        : {};
    const selectedIndex = useSharedValue(dataPoints.length > 0 ? dataPoints.length - 1 : 0);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getCreditScoreHistory());
    }, [dispatch]);
    useEffect(() => {
        if (dataPoints.length > 0) {
            selectedIndex.value = dataPoints.length - 1;
        }
    }, [dataPoints.length, selectedIndex]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: () => (<NavigationHeaderTitle title={strings.headerTitle}>
              {lastUpdateDate ? (<Text TextThin-14 Gray>
                  {formatDateForItem(lastUpdateDate, 'Last update', true)}
                </Text>) : null}
            </NavigationHeaderTitle>),
        });
    }, [navigation, lastUpdateDate]);
    const noData = dataPoints.length === 0;
    if (isFetching && noData) {
        return (<AppView withFrame={false}>
            <Indicator dark/>
          </AppView>);
    }
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle}>
            {noData ? (<ListEmptyView onCard happy title={strings.emptyTitle} subTitle={strings.emptySubTitle} style={styles.listEmptyView}/>) : (<>
                <CardView style={styles.card} noMarginBottom>
                  <ScrollingLineChart data={dataPoints} selectedIndex={selectedIndex} Header={CreditScoreHistoryHeader} {...groupingProps}/>
                </CardView>
                <CreditScoreHistoryFactors dataPoints={dataPoints} factorsByIndex={factorsByIndex} selectedIndex={selectedIndex} footer={<Footer />}/>
              </>)}
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    hasCustomTitle: true,
    headerHeight: HEADER_WITH_SUBTITLE_HEIGHT,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditScoreHistoryScreen;
const styles = StyleSheet.create({
    card: {
        paddingVertical: rem(16),
        marginBottom: rem(8),
    },
    section: {
        marginTop: rem(8),
    },
    listEmptyView: {
        marginTop: rem(24),
    },
});
